import yup from "yup";
import { action, computed, observable, toJS, runInAction } from "mobx";
import {
  cloneDeep,
  get,
  isEqual,
  clone,
  set,
  debounce,
  merge,
  isPlainObject
} from "lodash";
import { ValidationError, validate } from "class-validator";
import { ClassType, transformAndValidate } from "class-transformer-validator";
import { Field } from "./Field";
import {
  FormState,
  BaseFormData,
  FormErrors,
  FormFields,
  HTMLEvent,
  FieldOptions
} from "./types";
import {
  yupToFormErrors,
  validateYupSchema,
  isPromise,
  classValidatorToFormErrors
} from "./utils";

export type KeyValue<T, K extends keyof T> = { key: keyof T; field: Field<T[K]> };
export type ErrorSelector<T extends BaseFormData> = (x: FormErrors<T>) => any;
export type ValidateFunc<
  T extends BaseFormData,
  S extends FormStore<T> = FormStore<T>
> = (store: S) => FormErrors<T> | Promise<any> | void;

export interface FormStoreOptions<T extends BaseFormData> {
  classValidatorClassType?: ClassType<T>;
  exposeAs?: string;
  debounceValidationMs?: number;
  initialValues?: T;
  initialDirty?: boolean;
  initialDisabled?: boolean;
  onSetValue?: OnSetValueCallback<T>;
  onSubmit?: () => Promise<any>;
  onSubmitSuccess?: () => void;
  onSubmitFail?: () => void;
  schema?: yup.ObjectSchema<T>;
  validate?: ValidateFunc<T>;
}

export type OnSetValueParams<T extends BaseFormData, K extends keyof T = keyof T> = {
  name: K;
  value: T[K];
  prevValue: T[K];
  store: FormStore<T>;
};

export type OnSetValueCallback<T extends BaseFormData> = (
  options: OnSetValueParams<T>
) => void;

export const isField = <T extends BaseFormData, K extends keyof T>(
  x: OnSetValueParams<T>,
  name: K
): x is OnSetValueParams<T, K> => {
  return x.name === name;
};

export class FormStore<T extends BaseFormData> {
  @computed public get canSubmit() {
    return (
      this.dirty &&
      !this.isSubmitting &&
      !this.isValidating &&
      this.valid &&
      !this.disabled
    );
  }

  @computed public get clean() {
    return !this.dirty;
  }

  @computed public get disabled() {
    return this._disabled || this._isSubmitting;
  }

  public set disabled(v: boolean) {
    this._disabled = v;
  }

  @computed public get dirty() {
    const current = toJS(this._values.get());
    const initial = toJS(this._initialValues || {});

    // the problem here is that if the form never received initial values
    // via `this.values = {..}` then after a field was edited, we end up in a
    // situation when:
    // current = { someField: "" }
    // initial = {}
    return !isEqual(current, initial);
  }

  @computed public get focusedField() {
    const field = this.fieldsArray.find((f) => f.field.focused);
    return field ? field.field : null;
  }

  @computed public get invalid() {
    return !this.valid;
  }

  @computed public get isSubmitting() {
    return this._isSubmitting;
  }

  @computed public get isValidating() {
    return this._isValidating;
  }

  @computed public get touched() {
    return this.fieldsArray.reduce((p, c) => p || c.field.touched, false);
  }

  @computed public get valid() {
    if (!this.errors) {
      return true;
    }

    return Object.keys(this.errors).length === 0;
  }

  @computed public get values(): T {
    return this._values.get();
  }

  public set values(v: T) {
    this._values.set(v);
    this.validate();
  }

  @computed public get errors() {
    return this._errors.get();
  }

  public set errors(errs: FormErrors<T>) {
    this._errors.set(errs);
  }

  public get initialValues() {
    return toJS(this._initialValues);
  }

  @computed get state(): FormState<T> {
    return {
      clean: !this.dirty,
      dirty: this.dirty,
      errors: this.errors,
      initialValues: this.initialValues,
      invalid: this.invalid,
      isSubmitting: this.isSubmitting,
      isValidating: this.isValidating,
      pristine: !this.dirty,
      touched: this.touched,
      valid: this.valid,
      values: this.values
    };
  }

  @computed get stateJS() {
    return toJS(this.state);
  }

  @computed public get fields() {
    return this._fields;
  }

  @computed public get fieldsArray() {
    return Object.keys(this._fields).map((key) => ({
      key,
      field: this._fields[key]
    })) as Array<KeyValue<T, keyof T>>;
  }

  public set debounceValidationMs(v: number) {
    this._runValidationDebounced = v > 0 ? debounce(this._runValidation, v) : null;
  }

  public onSubmit?: () => Promise<any>;
  public onSubmitSuccess?: (res: any) => void;
  public onSubmitFail?: (e: any) => void;

  @observable error: any = null;
  @observable loading = true;
  protected _name: string = "";
  @observable private _isSubmitting = false;
  @observable private _isValidating = false;
  @observable private _disabled = false;
  @observable private _fields: FormFields<T> = {};
  private _values = observable.box<T>({} as any, { deep: true });
  private _errors = observable.box<FormErrors<T>>({});
  @observable private _initialValues: T = {} as T;
  @observable private _initialized = false;
  private _runValidationDebounced: () => Promise<any> = null;

  private onSetValueCallbacks: Array<OnSetValueCallback<T>> = [];

  constructor(private options: FormStoreOptions<T>) {
    this.debounceValidationMs = this.options.debounceValidationMs;
    this.getField = this.getField.bind(this);
    this.onResetHandler = this.onResetHandler.bind(this);
    this.submit = this.submit.bind(this);
    this.validate = this.validate.bind(this);
    this._disabled = Boolean(options.initialDisabled);

    if (options.onSubmit) {
      this.onSubmit = () => options.onSubmit();
    }

    if (options.onSubmitSuccess) {
      this.onSubmitSuccess = options.onSubmitSuccess;
    }

    if (options.onSubmitFail) {
      this.onSubmitFail = options.onSubmitFail;
    }

    if (options.initialValues) {
      options.initialDirty
        ? (this.values = options.initialValues)
        : this.reset(options.initialValues);
    }

    if (options.onSetValue) {
      this.onSetValueCallbacks.push(options.onSetValue);
    }

    if (options.exposeAs) {
      (window as any)[options.exposeAs] = this;
    }
  }

  // printError = (text string) => store().snackbars.error(text);
  // printSuccess = (text: string) => store().snackbars.success(text);

  // todo: remove this and accept initialValues in constructor options
  public initialize(v: T) {
    this._initialValues = clone(v);
    this._values.set(clone(v));
    this._initialized = true;
  }

  public changed() {
    (this._values as any).reportChanged();
  }

  public setSchema(schema: yup.ObjectSchema<T>) {
    this.options.schema = schema;
    this.validate();
  }

  public onSetValue(cb: OnSetValueCallback<T>) {
    if (this.onSetValueCallbacks.includes(cb)) {
      return;
    }

    this.onSetValueCallbacks.push(cb);
  }

  public getField(name: string) {
    return this._fields[name];
  }

  public getValue(name: string) {
    return get(this._values.get(), name);
  }

  public getInitialValue(name: string) {
    return get(toJS(this._initialValues), name);
  }

  public getError(selector: ErrorSelector<T>): string;
  public getError(name: string): string;
  public getError(arg: string | ErrorSelector<T>): string {
    if (typeof arg === "string") {
      return get(this._errors.get(), arg) as string;
    }

    if (typeof arg === "function") {
      return arg(this._errors.get() || {});
    }

    throw new Error("getError arg must be string or function");
  }

  public setValue(name: string, value: any) {
    const prevValue = this.getValue(name);
    set(this._values.get(), name, value);
    this._triggerOnSetValue(name, value, prevValue);
    this.validate();
  }

  public setError(name: string, err: any) {
    set(this._errors.get(), name, err);
  }

  public push(name: string, value: any) {
    const current = this.getValue(name);

    if (!current || !current.push) {
      throw new Error(`Cannot push to ${name}: field is not an array`);
    }

    this.setValue(name, current.concat(value));
  }

  public insert(name: string, value: any, index: number) {
    const current = this.getValue(name);

    if (!current || !current.push) {
      throw new Error(`Cannot insert to ${name}: field is not an array`);
    }

    const cloned = [...(current as any[])];
    cloned.splice(index, 0, value);
    this.setValue(name, cloned);
  }

  public removeByIndex(name: string, index: number) {
    const current = this.getValue(name);

    if (!current || !current.length) {
      throw new Error(`Cannot remove from ${name}: field is not an array`);
    }

    if (current.length < index) {
      throw new Error(`Cannot remove from ${name}: index is out of bounds`);
    }

    const cloned = [...(current as any[])];
    cloned.splice(index, 1);
    this.setValue(name, cloned);
  }

  @action.bound public setErrors(errors: Array<{ Path?: string; Message?: string }>) {
    if (!Array.isArray(errors)) {
      return;
    }
    errors.forEach((err) => this.setError(err.Path, err.Message));
  }

  public setSubmitting(value: boolean) {
    this._isSubmitting = value;
  }

  @action public submit(e?: HTMLEvent) {
    this.submitAsync(e);
  }

  @action public submitAsync(e?: HTMLEvent) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!this.onSubmit) {
      throw new Error("onSubmit handler is missing");
    }

    this._isSubmitting = true;
    this._disabled = true;

    return this.onSubmit()
      .then((res) => {
        this._isSubmitting = false;
        this.onSubmitSuccess && this.onSubmitSuccess(res);

        return res;
      })
      .catch((err) => {
        this._isSubmitting = false;

        if (this.onSubmitFail) {
          this.onSubmitFail(err);
        } else {
          throw err;
        }
      })
      .finally(() => {
        this.disabled = false;
      });
  }

  public onResetHandler() {
    this.reset();
  }

  @action.bound registerField<V, K extends keyof T>(
    name: K,
    fieldOptions: FieldOptions<V>
  ) {
    if (this._fields[name]) {
      return this._fields[name];
    }

    const field = new Field<T[K]>(this, name.toString(), fieldOptions);
    this._fields[name] = field;

    return this._fields[name];
  }

  @action.bound unregisterField(name: string) {
    delete this._fields[name];
  }

  @action.bound reset(values?: T, dontValidate?: boolean) {
    if (values != null) {
      this._initialValues = cloneDeep(values);
    }

    if (isPlainObject(values)) {
      this._values.set(values ? toJS(values) : clone(toJS(this._initialValues)));
    } else {
      this._values.set(values);
    }

    if (!dontValidate) {
      this.validate();
    }
  }

  @action.bound resetToCurrent() {
    this._initialValues = cloneDeep(toJS(this.values));
  }

  public validate() {
    if (this._runValidationDebounced) {
      return Promise.resolve(this._runValidationDebounced());
    }

    return this._runValidation();
  }

  @action.bound private _runValidation() {
    this._isValidating = true;

    return Promise.all([
      this.options.validate ? this._runValidateHandler() : {},
      this.options.schema ? this._runValidationSchema() : {},
      this.options.classValidatorClassType ? this._runClassValidationSchema() : {}
    ])
      .then(([handlerErrors, schemaErrors, classValidatorErrors]) => {
        const mergedErrors = merge(handlerErrors, schemaErrors, classValidatorErrors);

        runInAction(() => {
          this.errors = mergedErrors;
          this._isValidating = false;
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  private _runValidateHandler(): Promise<FormErrors<T>> {
    return new Promise((resolve) => {
      const maybePromisedErrors = this.options.validate(this);

      if (maybePromisedErrors === undefined) {
        resolve({});
      } else if (isPromise(maybePromisedErrors)) {
        maybePromisedErrors.then(
          () => {
            resolve({});
          },
          (errors) => {
            resolve(errors);
          }
        );
      } else {
        resolve(maybePromisedErrors as any as FormErrors<T>);
      }
    });
  }

  /**
   * Run validation against a Yup schema and optionally run a function if successful
   */
  private _runValidationSchema() {
    return new Promise((resolve) => {
      validateYupSchema(this.values, this.options.schema, false, this.values).then(
        () => {
          resolve({});
        },
        (err: any) => {
          resolve(yupToFormErrors(err));
        }
      );
    });
  }

  private _runClassValidationSchema() {
    return new Promise((resolve) => {
      const promise =
        this.values instanceof this.options.classValidatorClassType
          ? validate(this.values).then((errors) => {
              if (errors.length === 0) {
                return this.values;
              }

              throw errors;
            })
          : transformAndValidate(this.options.classValidatorClassType, this.values);

      promise
        .then(() => {
          resolve({});
        })
        .catch((err: ValidationError[]) => {
          resolve(classValidatorToFormErrors(err));
        });
    });
  }

  private _triggerOnSetValue(name: string, value: any, prevValue: any) {
    this.onSetValueCallbacks.forEach((cb) => cb({ name, value, prevValue, store: this }));
  }
}
